import { useTranslation } from "@/packages/utils";
import { Button } from "@/components/ui";
import { CustomerPortalLink } from "@convex-dev/polar/react";
import { api } from "convex/_generated/api";
import type { BillingTier } from "@/packages/domains/billing";

interface PlanCardProps {
  tier: BillingTier;
  subscription: {
    tier: BillingTier;
    status: string;
    productId?: string;
    currentPeriodEnd?: string | null;
    cancelAtPeriodEnd?: boolean;
  } | null;
}

const TIER_LABELS: Record<BillingTier, string> = {
  free: "Free",
  pro: "Professional",
  enterprise: "Enterprise",
};

const STATUS_STYLES: Record<string, { bg: string; text: string }> = {
  active: { bg: "rgba(34,197,94,0.12)", text: "#16a34a" },
  past_due: { bg: "rgba(239,68,68,0.12)", text: "#ef4444" },
  canceled: { bg: "rgba(156,163,175,0.12)", text: "#6b7280" },
};

export function PlanCard({ subscription, tier }: PlanCardProps) {
  const { t } = useTranslation(["settings"]);

  const status = subscription?.status ?? "active";
  const cancelAtPeriodEnd = subscription?.cancelAtPeriodEnd ?? false;
  const currentPeriodEnd = subscription?.currentPeriodEnd;

  const statusStyle = STATUS_STYLES[status] ?? STATUS_STYLES.active;

  const nextBillingDate = currentPeriodEnd
    ? new Date(currentPeriodEnd).toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      })
    : null;

  return (
    <div className="rounded-card border border-border p-5 bg-card">
      <div className="flex items-start justify-between mb-4">
        <div>
          <div className="flex items-center gap-2.5">
            <h3 className="text-[16px] font-semibold text-primary">
              {TIER_LABELS[tier]}
            </h3>
            <span
              className="inline-block rounded-full px-2.5 py-0.5 text-[11px] font-medium"
              style={{
                backgroundColor: statusStyle.bg,
                color: statusStyle.text,
              }}
            >
              {status === "past_due"
                ? "Past Due"
                : status.charAt(0).toUpperCase() + status.slice(1)}
            </span>
          </div>
          {tier === "free" && (
            <p className="text-[13px] text-secondary mt-1">
              {t("settings:billing.freePlanDescription")}
            </p>
          )}
        </div>
        {tier !== "free" && (
          <CustomerPortalLink
            polarApi={{
              generateCustomerPortalUrl: api.polar.generateCustomerPortalUrl,
            }}
          >
            <Button variant="primary" size="sm">
              {t("settings:billing.manageSubscription")}
            </Button>
          </CustomerPortalLink>
        )}
      </div>

      {cancelAtPeriodEnd && nextBillingDate && (
        <div className="mb-3 rounded-lg bg-warning-soft px-3 py-2">
          <p className="text-[12px] font-medium text-warning">
            {t("settings:billing.cancelsOn", { date: nextBillingDate })}
          </p>
        </div>
      )}

      {nextBillingDate && !cancelAtPeriodEnd && (
        <p className="text-[12px] text-muted">
          {t("settings:billing.nextBillingDate", { date: nextBillingDate })}
        </p>
      )}
    </div>
  );
}
